import { type FC, useEffect, useState } from "react";
import {
  Alert,
  AppBar,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useDeleteContentsMutation } from "@/partner/entities/contents";
import { formatUsdCost, formatUsdPerMonth } from "@/partner/entities/storage";
import { useIsBunnyProvider } from "@/partner/entities/platforms";
import { useIsMobile } from "@/shared/hooks";
import type { StorageItem } from "./Storage";

const PREVIEW_COUNT = 10;
const BATCH_SIZE = 50;

interface DeleteStorageDialogProps {
  items: StorageItem[] | null;
  onClose: () => void;
  onDeleted: () => void;
}

/**
 * Диалог bulk-удаления выбранных строк «Хранилища» с двойным
 * подтверждением: пользователь должен вписать количество выбранных
 * позиций, прежде чем кнопка «Удалить» разблокируется. Составное тело
 * (список + алерты + поле + прогресс) ⇒ эталонная шапка AppBar/Toolbar/
 * Divider из docs/CRITICAL-RULES.md (голый DialogTitle запрещён).
 *
 * Удаление идёт через useDeleteContentsMutation (DeleteContents,
 * { ids: [ID!]! }) императивно пачками по BATCH_SIZE — последовательно,
 * чтобы не упереться в лимиты бэкенда на bulk-мутациях. refetchQueries
 * на каждый вызов обновляет и список, и итоги (StorageBreakdown).
 */
export const DeleteStorageDialog: FC<DeleteStorageDialogProps> = ({
  items,
  onClose,
  onDeleted,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useIsMobile();
  const isBunny = useIsBunnyProvider();

  const [confirmValue, setConfirmValue] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [done, setDone] = useState(0);
  const [partialError, setPartialError] = useState(false);

  // items — новый массив при каждом открытии диалога (Storage.tsx кладёт
  // выбранные строки в pendingDeletion) — сбрасываем локальное состояние,
  // сам компонент не размонтируется между открытиями.
  useEffect(() => {
    if (items) {
      setConfirmValue("");
      setDeleting(false);
      setDone(0);
      setPartialError(false);
    }
  }, [items]);

  const [deleteContents] = useDeleteContentsMutation({
    refetchQueries: ["StorageBreakdown"],
  });

  const list = items ?? [];
  const total = list.length;
  const preview = list.slice(0, PREVIEW_COUNT);
  const moreCount = total - preview.length;
  const savings = list.reduce((sum, item) => sum + item.storageCostUsdMonth, 0);
  // Экономия по тарифу Bunny это центы: на двух знаках она превратилась бы
  // в $0.00 (см. formatUsdCost).
  const formatSavings = isBunny ? formatUsdCost : formatUsdPerMonth;
  const soldItems = list.filter((item) => item.onSale || item.hasPurchases);

  const confirmed = total > 0 && confirmValue.trim() === String(total);

  const handleClose = () => {
    if (deleting) return;
    onClose();
  };

  const handleDelete = async () => {
    if (!confirmed) return;

    setDeleting(true);
    setPartialError(false);
    const ids = list.map((item) => item.contentId);

    try {
      for (let offset = 0; offset < ids.length; offset += BATCH_SIZE) {
        const batch = ids.slice(offset, offset + BATCH_SIZE);
        await deleteContents({ variables: { ids: batch } });
        setDone(Math.min(offset + BATCH_SIZE, ids.length));
      }

      enqueueSnackbar(
        t("storage.delete.done", {
          count: total,
          amount: formatSavings(savings),
        }),
        { variant: "success" }
      );
      onDeleted();
    } catch (error) {
      // Уже удалённые пачки не откатываются — refetchQueries на успешных
      // вызовах уже обновил список/итоги, поэтому диалог остаётся открытым
      // с предупреждением, а не молча закрывается как после полного успеха.
      setPartialError(true);
      enqueueSnackbar((error as Error).message, { variant: "error" });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog
      open={Boolean(items)}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
    >
      <AppBar color="default" position="sticky" elevation={0}>
        <Toolbar>
          <IconButton
            edge="start"
            onClick={handleClose}
            disabled={deleting}
            size="large"
            sx={{ mr: 1 }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flex: 1, mr: 1 }}>
            {t("storage.delete.title")}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button onClick={handleClose} disabled={deleting}>
              {t("storage.delete.cancel")}
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={!confirmed || deleting}
              onClick={handleDelete}
            >
              {t("storage.delete.submit")}
            </Button>
          </Stack>
        </Toolbar>
        <Divider />
      </AppBar>

      <DialogContent sx={{ p: 1 }}>
        <Card elevation={0}>
          <CardContent>
            <List sx={{ py: 0 }}>
              {preview.map((item) => (
                <ListItem key={item.contentId} sx={{ px: 0 }}>
                  <ListItemText
                    primary={item.title}
                    sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                  />
                </ListItem>
              ))}
            </List>
            {moreCount > 0 && (
              <Typography variant="body2" color="text.secondary">
                {t("storage.delete.andMore", { count: moreCount })}
              </Typography>
            )}

            <Typography variant="body1" sx={{ mt: 2 }}>
              {t("storage.delete.savings", {
                amount: formatSavings(savings),
              })}
            </Typography>

            {soldItems.length > 0 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {t("storage.delete.soldWarning", {
                  count: soldItems.length,
                })}
              </Alert>
            )}

            {partialError && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {t("storage.delete.partialError")}
              </Alert>
            )}

            <TextField
              fullWidth
              sx={{ mt: 2 }}
              label={t("storage.delete.confirmLabel", { count: total })}
              value={confirmValue}
              onChange={(event) => setConfirmValue(event.target.value)}
              disabled={deleting}
            />

            {deleting && (
              <>
                <LinearProgress
                  variant="determinate"
                  value={total > 0 ? (done / total) * 100 : 0}
                  sx={{ mt: 2 }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {t("storage.delete.progress", { done, total })}
                </Typography>
              </>
            )}
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
};
